/**
 * sharePost — One-tap share helper for schedule-post PNGs.
 *
 * Native iOS / Android (Capacitor):
 *   - Saves the PNG to the app's Cache directory via @capacitor/filesystem
 *   - Opens the OS share sheet via @capacitor/share with `files: [<uri>]`
 *   - Result: user taps Instagram / Messages / WhatsApp / "Save to Photos"
 *
 * Web (browser):
 *   - Tries the Web Share API with files (works on iOS Safari, Android
 *     Chrome — pops the native sheet)
 *   - Falls back to a plain `<a download>` so the user can attach the PNG
 *     to a social post manually
 *
 * Detection rule: Capacitor.isNativePlatform() — the only reliable way
 * to distinguish "running inside the iOS/Android shell" vs "browser tab".
 */
import { Capacitor } from "@capacitor/core";
import { BACKEND_BASE } from "./backendBase";
import { Share } from "@capacitor/share";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { toast } from "sonner";

/**
 * @param {Blob} blob       The PNG blob to share
 * @param {string} filename Suggested filename (used by some share targets)
 * @param {string} title    Share-sheet title hint
 * @param {string} text     Pre-filled caption (Twitter/X uses this)
 * @param {string} truckId  Optional — when present, fires an analytics
 *                          `share_view` event so the truck rolls up into
 *                          the "Trending — getting lots of shares this
 *                          week" map chip (≥10 shares in 7 days).
 */
export async function sharePost({ blob, filename, title, text, truckId }) {
  if (!blob) {
    toast.error("Nothing to share yet");
    return;
  }

  // -------------------------------------------------------------------
  // Native (Capacitor) path — share a real file URI so apps like
  // Instagram can ingest the image directly (Web Share API on iOS
  // Safari is unreliable for files; the native plugin is the only
  // production-grade option inside Capacitor).
  // -------------------------------------------------------------------
  if (Capacitor.isNativePlatform()) {
    try {
      const base64 = await blobToBase64(blob);
      // Write to Cache (NOT Documents) — auto-purged by iOS, doesn't
      // require user-visible "saved to Files" semantics.
      const written = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      });
      const uri = written.uri;
      await Share.share({
        title: title || "TruckTrackr schedule post",
        text: text || "",
        url: uri,        // iOS uses .url with a file:// scheme
        files: [uri],    // Android uses .files
        dialogTitle: "Share schedule post",
      });
    } catch (e) {
      // User canceled is the most common error — silently swallow
      const msg = String(e?.message || e);
      if (!/cancel/i.test(msg)) {
        console.warn("native share failed", e);
        toast.error("Share failed — try Download instead");
      }
    }
    trackShareEvent(truckId);
    return;
  }

  // -------------------------------------------------------------------
  // Web path — Web Share API with files (modern mobile browsers) →
  // fallback to download. canShare({ files }) is the gate; some
  // browsers expose Share but reject files.
  // -------------------------------------------------------------------
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ title, text, files: [file] });
      trackShareEvent(truckId);
      return;
    } catch (e) {
      // AbortError = user dismissed; not an error worth toasting
      if (e?.name === "AbortError") return;
      console.warn("web share failed, falling back to download", e);
    }
  }

  // Plain download fallback
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  trackShareEvent(truckId);
  toast.info("Share sheet not available — image downloaded so you can post manually");
}

/**
 * Fire-and-forget analytics ping so the truck's `is_trending` rollup
 * picks up this share. Backend dedupes per (truck_id, session, day) so
 * spamming the share button doesn't game the leaderboard. Failures are
 * silently swallowed — share UX must never block on telemetry.
 */
function trackShareEvent(truckId) {
  if (!truckId) return;
  try {
    const base = BACKEND_BASE || "";
    const sessionId = (() => {
      try {
        let s = localStorage.getItem("tt_session_id");
        if (!s) {
          s = Math.random().toString(36).slice(2) + Date.now().toString(36);
          localStorage.setItem("tt_session_id", s);
        }
        return s;
      } catch { return undefined; }
    })();
    fetch(`${base}/api/analytics/track`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        truck_id: truckId,
        event_type: "share_view",
        session_id: sessionId,
        meta: { source: "schedule_post_share" },
      }),
    }).catch(() => {});
  } catch {/* swallow */}
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // strip the "data:image/png;base64," prefix — Filesystem.writeFile
      // wants raw base64 when encoding is omitted.
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convenience: fetch + share in one call. Caller passes the schedule-post
 * download URL; we GET the PNG and pipe it through `sharePost`.
 */
export async function fetchAndShare({ url, token, filename, title, text, truckId }) {
  try {
    const res = await fetch(url, {
      cache: "no-store",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`download ${res.status}`);
    const blob = await res.blob();
    // Honour server-suggested filename when available.
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const fname = m?.[1] || filename || "schedule-post.png";
    await sharePost({ blob, filename: fname, title, text, truckId });
  } catch (e) {
    console.warn("fetchAndShare failed", e);
    toast.error("Share failed — try Download instead");
  }
}
